import React, { useId } from "react";
import cn from "classnames";
import { BasicModal } from "../BasicModal/BasicModal";
import { ReactComponent as CloseIcon } from "../CancelKeywordModal/close-icon.svg";
import styles from "./styles.module.scss";

type Props = {
  isOpened: boolean;
  title: React.ReactNode;
  /** The question under the title; a <strong> in it starts a line of its own. */
  children?: React.ReactNode;
  cancelText?: React.ReactNode;
  confirmText: React.ReactNode;
  /** danger for removals, primary for a go-ahead ("Create order?"). */
  confirmVariant?: "danger" | "primary";
  onConfirm: () => void;
  onCancel: () => void;
  /** The cross, the backdrop and Escape; Cancel when not given. */
  onClose?: () => void;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
};

/**
 * The one confirmation dialog of the app, drawn as the DS "Modal - Default"
 * (Figma 4329:18503 default, 4329:18541 hover, 4323:6559 busy): a title, an
 * optional question and two even buttons sharing the row. Removals,
 * "Create order?" and the like all render it, so a phone sees the same box
 * everywhere.
 */
export const ConfirmModal = ({
  isOpened,
  title,
  children,
  cancelText = "Cancel",
  confirmText,
  confirmVariant = "danger",
  onConfirm,
  onCancel,
  onClose,
  confirmDisabled = false,
  cancelDisabled = false,
}: Props) => {
  const titleId = useId();
  const close = onClose ?? onCancel;

  return (
    <BasicModal
      isOpened={isOpened}
      closeModal={close}
      className={styles.modal}
      hideCloseButton
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className={styles.close}
          onClick={close}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
        <h2 className={styles.title} id={titleId}>
          {title}
        </h2>
        {children && <div className={styles.message}>{children}</div>}
        <div className={styles.buttons}>
          <button
            type="button"
            className={cn(styles.button, styles.cancel)}
            onClick={onCancel}
            disabled={cancelDisabled}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={cn(styles.button, styles[confirmVariant])}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </BasicModal>
  );
};

export default ConfirmModal;
